/**
 * PortalAccountPage — the Account hub (bottom-nav "Account" destination).
 *
 * A clean summary + grouped action lists, replacing the old all-in-one
 * profile screen. The detailed health-profile form (DOB, allergies,
 * conditions, emergency contact, preferred pharmacy, notifications) now
 * lives one tap away at "Edit profile" (`PortalProfilePage`, routed as
 * `edit-profile`) — every existing field, save call and validation rule is
 * untouched, just relocated.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronRight, Download, LogOut, ShieldCheck, Clock, AlertTriangle, Pencil, FileDown, Trash2 } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { usePortalAuth } from '../PortalAuthContext';
import { usePortalDestination } from '../PortalDestinationContext';
import { usePortalInstallPrompt } from '../lib/pwa';
import { PortalDoctorLink } from '../PortalDoctorLink';
import { ACCOUNT_GROUPS } from '../portalNavigation';
import { PatientAppInstallOffer } from '../ui/PatientAppInstallOffer';

const identityApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

const IDENTITY_META = {
  verified:        { label: 'Verified',     icon: ShieldCheck,   cls: 'is-verified' },
  pending_review:  { label: 'Pending',       icon: Clock,         cls: 'is-pending' },
  photo_requested: { label: 'Action needed', icon: AlertTriangle, cls: 'is-action' },
  rejected:        { label: 'Action needed', icon: AlertTriangle, cls: 'is-action' },
};
const NOT_STARTED = { label: 'Not verified', icon: ShieldCheck, cls: 'is-none' };

function AccountRow({ to, icon: Icon, label, value, valueAccent, testId, onClick, danger }) {
  const inner = (
    <>
      <Icon size={20} aria-hidden="true" />
      <span className="portal-row-label">{label}</span>
      {value && <span className={`portal-row-value${valueAccent ? ' is-accent' : ''}`} data-testid={`${testId}-value`}>{value}</span>}
      {to && <ChevronRight size={17} aria-hidden="true" />}
    </>
  );
  const className = `portal-more-row${danger ? ' portal-sign-out' : ''}`;
  if (to) return <Link to={to} className={className} data-testid={testId}>{inner}</Link>;
  return <button type="button" className={className} onClick={onClick} data-testid={testId}>{inner}</button>;
}

export default function PortalAccountPage() {
  const { patient, logout } = usePortalAuth();
  const { pharmacy, href, api: portalApi } = usePortalDestination();
  const navigate = useNavigate();
  const { canInstall, install } = usePortalInstallPrompt();
  const [identity, setIdentity] = useState(undefined); // undefined=loading, null=not started, object=record
  const [stats, setStats] = useState({ reminders: null, orders: null, points: null });
  const [showInstallOptions, setShowInstallOptions] = useState(false);

  useEffect(() => {
    let live = true;
    if (!pharmacy?.tenant_id) { setIdentity(null); return undefined; }
    identityApi.get('/identity/me', { params: { pharmacy_id: pharmacy.tenant_id } })
      .then((r) => { if (live) setIdentity(r.data.verification || null); })
      .catch(() => { if (live) setIdentity(null); });
    return () => { live = false; };
  }, [pharmacy?.tenant_id]);

  useEffect(() => {
    let live = true;
    Promise.all([
      portalApi.get('/reminders').catch(() => ({ data: { reminders: [] } })),
      portalApi.get('/orders').catch(() => ({ data: { orders: [] } })),
      portalApi.get('/loyalty').catch(() => ({ data: { accounts: [] } })),
    ]).then(([rem, ord, loy]) => {
      if (!live) return;
      setStats({
        reminders: (rem.data.reminders || []).filter((r) => r.active !== false).length,
        orders: (ord.data.orders || []).length,
        points: (loy.data.accounts || []).reduce((sum, a) => sum + (a.balance || 0), 0),
      });
    });
    return () => { live = false; };
  }, [portalApi]);

  const doLogout = async () => {
    const demo = !!patient?.is_demo_account;
    await logout();
    if (demo) window.location.assign('/try-demo');
    else navigate(href('login'), { replace: true });
  };

  const requestMyData = async (type) => {
    if (!pharmacy?.tenant_id) { toast.error('Pick a pharmacy first, from your home screen, then try again.'); return; }
    if (type === 'delete' && !window.confirm('This asks your pharmacy to delete your account data. Continue?')) return;
    try {
      await portalApi.post('/me/data-request', { type, tenant_id: pharmacy.tenant_id });
      toast.success(type === 'export'
        ? 'Request received — your pharmacy will prepare your data export.'
        : 'Request received — your pharmacy will review and confirm the deletion.');
    } catch {
      toast.error('Could not send that request — please try again.');
    }
  };

  const identityMeta = identity === undefined ? null : (identity ? (IDENTITY_META[identity.status] || NOT_STARTED) : NOT_STARTED);

  const valueFor = (id) => {
    if (id === 'reminders') return stats.reminders > 0 ? `${stats.reminders} active` : null;
    if (id === 'orders') return stats.orders > 0 ? `${stats.orders}` : null;
    if (id === 'rewards') return stats.points != null ? `${stats.points.toLocaleString()} pts` : null;
    if (id === 'verify-id') return identityMeta?.label || null;
    return null;
  };

  const contact = patient?.email && patient?.phone ? `${patient.email} · ${patient.phone}`
    : patient?.email || patient?.phone || 'No contact on file';

  return (
    <div className="space-y-1" data-testid="portal-account-page">
      {pharmacy?.slug && !window.matchMedia('(display-mode: standalone)').matches && !navigator.standalone && <button type="button" className="portal-more-row" onClick={() => setShowInstallOptions(true)} data-testid="portal-manual-install-options"><Download size={20} /><span>Installation options</span></button>}
      {showInstallOptions && <PatientAppInstallOffer onDismiss={() => setShowInstallOptions(false)} />}
      <div className="flex items-start gap-3" data-testid="portal-account-header">
        <Avatar className="h-14 w-14 shrink-0"><AvatarImage src={patient?.avatar_url || ''} alt="" />
          <AvatarFallback className="bg-emerald-100 text-emerald-700 text-lg">{(patient?.name || 'P')[0].toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-lg text-slate-900 truncate" data-testid="portal-account-name">{patient?.name || 'Your account'}</div>
          <div className="text-sm text-slate-500 truncate" data-testid="portal-account-contact">{contact}</div>
          {identityMeta && (
            <Link to={href('verify-id')} className={`portal-identity-chip ${identityMeta.cls}`} data-testid="portal-account-identity-chip">
              <identityMeta.icon size={12} aria-hidden="true" /> {identityMeta.label}
            </Link>
          )}
        </div>
      </div>

      <Link to={href('edit-profile')} className="portal-account-edit-row" data-testid="portal-account-edit-profile">
        <span className="flex items-center gap-2"><Pencil size={16} aria-hidden="true" /> Edit profile</span>
        <ChevronRight size={16} aria-hidden="true" />
      </Link>

      <div className="space-y-1" data-testid="portal-account-groups">
        {ACCOUNT_GROUPS.map((group) => (
          <section key={group.id}>
            <h2 className="portal-account-group-title">{group.title}</h2>
            <div className="portal-more-links" data-testid={`portal-account-group-${group.id}`}>
              {group.items.map((item) => (
                <AccountRow
                  key={item.id}
                  to={`${href(item.page)}${item.search || ''}`}
                  icon={item.icon}
                  label={item.label}
                  value={valueFor(item.id)}
                  valueAccent={item.id === 'rewards'}
                  testId={`portal-account-${item.id}`}
                />
              ))}
              {group.id === 'my-care' && <PortalDoctorLink mobile />}
            </div>
          </section>
        ))}

        <section>
          <h2 className="portal-account-group-title">Settings and support</h2>
          <div className="portal-more-links" data-testid="portal-account-group-settings">
            {canInstall && <AccountRow icon={Download} label="Install app" testId="portal-account-install" onClick={install} />}
            <AccountRow icon={FileDown} label="Download my data" testId="portal-account-export-data" onClick={() => requestMyData('export')} />
            <AccountRow icon={Trash2} label="Delete my account" testId="portal-account-delete-data" onClick={() => requestMyData('delete')} danger />
            <AccountRow icon={LogOut} label="Sign out" testId="portal-account-logout" onClick={doLogout} danger />
          </div>
        </section>
      </div>
    </div>
  );
}
